import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Box, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardContent,
  Container,
  Fade,
  alpha,
  useTheme,
  GlobalStyles,
  CircularProgress,
  Chip,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';
import PublicIcon from '@mui/icons-material/Public';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip } from 'recharts';
import { currencyService, popularCurrencies } from '../services/api';
import CurrencyConverter from '../components/CurrencyConverter';

// Global animations for glass effects
const globalAnimations = {
  '@keyframes gradientFlow': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' }
  },
  '@keyframes float': {
    '0%, 100%': { transform: 'translateY(0px)' },
    '50%': { transform: 'translateY(-10px)' }
  },
  '@keyframes fadeIn': {
    '0%': { opacity: 0 },
    '100%': { opacity: 1 }
  }
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1'];

/**
 * Home page component with hero converter and popular rates
 */
const HomePage = () => {
  const [popularPairs, setPopularPairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const theme = useTheme();
  
  // Fetch popular pairs on page load
  useEffect(() => {
    const fetchPopularPairs = async () => {
      try {
        setLoading(true);
        const response = await currencyService.getPopularPairs();
        if (response.success) {
          setPopularPairs(response.pairs);
        }
      } catch (error) {
        console.error('Error fetching popular pairs:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPopularPairs();
  }, []);
  
  // Prepare chart data for popular currencies
  const getPopularCurrenciesData = () => {
    return popularCurrencies.slice(0, 6).map((currency, index) => ({
      name: currency.code,
      value: Math.random() * 100 + 50, // Mock data
      fullName: currency.name
    }));
  };
  
  // Generate mock trend data
  const getTrendData = () => {
    const data = [];
    for (let i = 7; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      data.push({
        date: date.getDate(),
        value: Math.random() * 20 + 80
      });
    }
    return data;
  };
  
  return (
    <>
      <GlobalStyles styles={globalAnimations} />
      <Helmet>
        <title>RatePly - Currency Converter & Exchange Rate Tool</title>
        <meta name="description" content="Convert currencies with real-time exchange rates. Fast, accurate, and free currency conversion tool supporting 170+ currencies." />
      </Helmet>
      
      {/* Hero Section */}
      <Box 
        sx={{
          py: 8,
          borderRadius: 3,
          mb: 6,
          textAlign: 'center',
          boxShadow: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.8)} 0%, ${alpha(theme.palette.primary.main, 0.7)} 100%)`,
          backdropFilter: 'blur(10px)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.3)} 0%, transparent 70%)`,
            animation: 'float 15s ease-in-out infinite',
            zIndex: 0
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -150,
            left: -150,
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(theme.palette.primary.light, 0.2)} 0%, transparent 70%)`,
            animation: 'float 18s ease-in-out infinite reverse',
            zIndex: 0
          }
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Fade in={true} timeout={800}>
            <Typography 
              variant="h2" 
              component="h1" 
              gutterBottom 
              sx={{ 
                fontWeight: 800,
                mb: 2,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                background: `linear-gradient(45deg, ${theme.palette.common.white}, ${alpha(theme.palette.secondary.light, 0.8)})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)'
              }}
            >
              Currency Converter & Exchange Rates
            </Typography>
          </Fade>
          
          <Fade in={true} timeout={1000}>
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 4,
                fontWeight: 400,
                opacity: 0.9,
                maxWidth: 800,
                mx: 'auto',
                color: 'white'
              }}
            >
              Get real-time exchange rates and convert currencies instantly. 
              Supporting 170+ currencies worldwide with live market data.
            </Typography>
          </Fade>
          
          <Fade in={true} timeout={1200}>
            <Box sx={{ 
              p: 2, 
              maxWidth: 700, 
              mx: 'auto'
            }}>
              <CurrencyConverter />
            </Box>
          </Fade>
          
          <Fade in={true} timeout={1400}>
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Chip 
                icon={<SwapHorizIcon />} 
                label="Instant Conversion" 
                sx={{ 
                  bgcolor: alpha(theme.palette.background.paper, 0.2),
                  backdropFilter: 'blur(8px)',
                  color: 'white',
                  '& .MuiChip-icon': { 
                    color: 'white' 
                  }
                }} 
              />
              <Chip 
                icon={<TrendingUpIcon />} 
                label="Live Rates" 
                sx={{ 
                  bgcolor: alpha(theme.palette.background.paper, 0.2),
                  backdropFilter: 'blur(8px)',
                  color: 'white',
                  '& .MuiChip-icon': { 
                    color: 'white' 
                  }
                }} 
              />
              <Chip 
                icon={<PublicIcon />} 
                label="170+ Currencies" 
                sx={{ 
                  bgcolor: alpha(theme.palette.background.paper, 0.2),
                  backdropFilter: 'blur(8px)',
                  color: 'white',
                  '& .MuiChip-icon': { 
                    color: 'white' 
                  }
                }} 
              />
              <Chip 
                icon={<AccessTimeIcon />} 
                label="Real-time Data" 
                sx={{ 
                  bgcolor: alpha(theme.palette.background.paper, 0.2),
                  backdropFilter: 'blur(8px)',
                  color: 'white',
                  '& .MuiChip-icon': { 
                    color: 'white' 
                  }
                }} 
              />
            </Box>
          </Fade>
        </Container>
      </Box>
      
      {/* Popular Pairs Section */}
      <Box sx={{ mb: 6 }}>
        <Typography 
          variant="h4" 
          component="h2" 
          gutterBottom 
          sx={{ 
            mb: 4, 
            textAlign: 'center',
            fontWeight: 600,
            color: 'white'
          }}
        >
          Popular Currency Pairs
        </Typography>
        
        <Grid container spacing={4}>
          {/* Popular Pairs Table */}
          <Grid item xs={12} md={8}>
            <Card elevation={4} sx={{ 
              height: '100%',
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
            }}>
              <CardContent>
                <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                  Live Exchange Rates
                </Typography>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {popularPairs.map((pair, index) => (
                      <Grid item xs={12} sm={6} key={`${pair.from}-${pair.to}`}>
                        <Box sx={{ 
                          p: 2, 
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          borderRadius: 2,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                            transform: 'translateY(-2px)',
                          },
                          transition: 'all 0.2s ease-in-out',
                        }}>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {pair.from}/{pair.to}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {popularCurrencies.find(c => c.code === pair.from)?.name} to {popularCurrencies.find(c => c.code === pair.to)?.name}
                            </Typography>
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                            {pair.rate}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          {/* Currency Distribution Chart */}
          <Grid item xs={12} md={4}>
            <Card elevation={4} sx={{ 
              height: '100%',
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
            }}>
              <CardContent sx={{ height: '100%' }}>
                <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                  Popular Currencies
                </Typography>
                <Box sx={{ height: 300, pt: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={getPopularCurrenciesData()}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name }) => name}
                      >
                        {getPopularCurrenciesData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name, props) => [value.toFixed(1), props.payload.fullName]} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      
      {/* Features Section */}
      <Box sx={{ mb: 6 }}>
        <Typography 
          variant="h4" 
          component="h2" 
          gutterBottom 
          sx={{ 
            mb: 4, 
            textAlign: 'center',
            fontWeight: 600,
            color: 'white'
          }}
        >
          Why Choose RatePly?
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              height: '100%', 
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 15px 35px rgba(0, 0, 0, 0.15)'
              },
            }}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <SpeedIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }} />
                <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                  Instant Conversion
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Get currency conversions in real-time with our lightning-fast API.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              height: '100%', 
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 15px 35px rgba(0, 0, 0, 0.15)'
              },
            }}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <SecurityIcon sx={{ fontSize: 48, color: theme.palette.secondary.main, mb: 2 }} />
                <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                  Accurate Rates
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Bank-grade accuracy with data from trusted financial sources.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              height: '100%', 
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 15px 35px rgba(0, 0, 0, 0.15)'
              },
            }}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <PublicIcon sx={{ fontSize: 48, color: theme.palette.success.main, mb: 2 }} />
                <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                  Global Coverage
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Support for 170+ currencies from around the world.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              height: '100%', 
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 15px 35px rgba(0, 0, 0, 0.15)'
              },
            }}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <AccessTimeIcon sx={{ fontSize: 48, color: theme.palette.warning.main, mb: 2 }} />
                <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                  Always Updated
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Exchange rates updated every hour to ensure accuracy.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      
      {/* CTA Section */}
      <Box 
        sx={{ 
          textAlign: 'center', 
          py: 6, 
          px: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, ${alpha(theme.palette.secondary.main, 0.03)} 100%)`,
          backdropFilter: 'blur(10px)',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'white' }}>
            Ready to Convert Currencies?
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, maxWidth: 700, mx: 'auto', color: alpha(theme.palette.common.white, 0.8) }}>
            Start using our advanced currency converter with real-time rates and historical data.
          </Typography>
          <Button 
            variant="contained" 
            size="large" 
            onClick={() => navigate('/converter')}
            sx={{ 
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundSize: '200% 200%',
              animation: 'gradientFlow 5s ease infinite',
              boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)',
              '&:hover': {
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.3)',
              }
            }}
          >
            Start Converting
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default HomePage;
